import type { ObjectTag } from '@atlas/types'
import { AxisCode } from '@atlas/types'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AddCatalogEntry } from '@/components/catalog/AddCatalogEntry'
import type { BrandPage as BrandPageData } from '@/domain/explore.types'
import { useApplicableCampaigns } from '@/hooks/useCampaigns'
import { useProductLogos } from '@/hooks/useExplore'
import { useThumbnails } from '@/hooks/useThumbnails'
import { ActiveCampaignsCard } from './ActiveCampaignsCard'
import { ProductIndex, type ProductIndexItem } from './ProductIndex'

export interface BrandProductsCampaignsTabProps {
	page: BrandPageData
	/** The product search, held by the page: an inactive tab panel is unmounted, and a
	 * search the reader typed must not be lost to a look at another tab. */
	query: string
	onQueryChange: (query: string) => void
	/** The audience pick, held by the page for the same reason as `query`. */
	audience: string
	onAudienceChange: (audience: string) => void
}

const labelsOn = (tags: ObjectTag[], axisCodes: string[]): string[] => [
	...new Set(tags.filter((t) => axisCodes.includes(t.axisCode)).map((t) => t.label)),
]

/** A product's display meta: its species tags, then its own subtitle. */
const productMeta = (subtitle: string | null, tags: ObjectTag[]): string =>
	[...labelsOn(tags, [AxisCode.SPECIES]), subtitle].filter(Boolean).join(' · ')

const toIndexItems = (page: BrandPageData, logoUrls: Record<string, string | null>): ProductIndexItem[] =>
	page.products.map((p) => {
		const tags = page.productTags[p.productId] ?? []
		return {
			productId: p.productId,
			name: p.name,
			// Keyed by CODE: the logo is discovered by classification, and a classification
			// names the axis value's code, not the catalog row's id.
			imageUrl: logoUrls[p.code] ?? null,
			meta: productMeta(p.subtitle, tags),
			// Value CODES, not labels: the audience pills filter on codes.
			audienceCodes: tags.filter((t) => t.axisCode === AxisCode.AUDIENCE).map((t) => t.valueCode),
		}
	})

/**
 * What the brand is SOLD as: the products under it, and the campaigns running for them.
 *
 * One tab for both because a campaign is the context its products' deliverables are produced
 * for — reading them apart would separate the work from what it is for. The products lead, so
 * the campaigns read against a catalogue the reader has already seen.
 */
export function BrandProductsCampaignsTab({
	page,
	query,
	onQueryChange,
	audience,
	onAudienceChange,
}: BrandProductsCampaignsTabProps) {
	const navigate = useNavigate()
	const campaigns = useApplicableCampaigns(null, page.brand.code)
	const productCodes = useMemo(() => page.products.map((p) => p.code), [page.products])
	const { data: logos = {} } = useProductLogos(productCodes, page.market?.code ?? null)
	const logoFiles = useMemo(() => Object.values(logos), [logos])
	const logoThumbnails = useThumbnails(logoFiles)
	const logoUrls = useMemo(() => {
		const byCode: Record<string, string | null> = {}
		for (const [code, file] of Object.entries(logos)) byCode[code] = logoThumbnails[file.fileId] ?? null
		return byCode
	}, [logos, logoThumbnails])

	return (
		<div className="space-y-4">
			{/* Preselected to the brand in view: adding a product from a brand's own page
			    almost always means adding it to that brand. */}
			<div className="flex justify-end">
				<AddCatalogEntry defaultBrandId={page.brand.brandId} />
			</div>
			<ProductIndex
				products={toIndexItems(page, logoUrls)}
				audiences={page.audiences}
				query={query}
				onQueryChange={onQueryChange}
				audience={audience}
				onAudienceChange={onAudienceChange}
				// The market carries into the product page only when one is applied.
				market={page.market?.code ?? null}
			/>
			<ActiveCampaignsCard
				campaigns={campaigns.campaigns}
				onOpenCampaign={(campaignId) => void navigate(`/campaigns/${campaignId}`)}
				onViewAllCampaigns={() => void navigate('/campaigns')}
				isLoading={campaigns.isLoading}
				isError={campaigns.isError}
			/>
		</div>
	)
}
